import { useEffect, useState } from 'react';
import { FileText, RotateCcw, Trash2, Download } from 'lucide-react';
import { useStore } from '../store';
import { Button, PageLoader } from '../components/ui';
import { api, isSampleMode } from '../api/client';
import { sampleHistory, sampleReport } from '../sample';

function downloadJson(filename: string, data: unknown): void {
  const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const STATUS: Record<string, { label: string; color: string }> = {
  completed: { label: 'Complete', color: '#00D700' },
  in_progress: { label: 'In progress', color: '#E8A400' },
  created: { label: 'Not started', color: '#75757F' },
  abandoned: { label: 'Abandoned', color: '#75757F' },
  expired: { label: 'Expired', color: '#75757F' },
  archived: { label: 'Archived', color: '#75757F' },
};

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function History() {
  const history = useStore((s) => s.history);
  const loading = useStore((s) => s.historyLoading);
  const loadHistory = useStore((s) => s.loadHistory);
  const openReport = useStore((s) => s.openReport);
  const resumeAssessment = useStore((s) => s.resumeAssessment);
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => loadHistory(), [loadHistory]);

  // DSAR erasure: delete one assessment and everything under it. Optimistic in sample mode.
  const onDelete = async (id: string) => {
    if (!window.confirm('Permanently delete this assessment and all its data? This cannot be undone.')) return;
    if (isSampleMode) {
      setHidden((h) => new Set(h).add(id));
      return;
    }
    setBusy(true);
    setNotice(null);
    try {
      await api.deleteAssessment(id);
      setHidden((h) => new Set(h).add(id));
    } catch {
      setNotice('Could not delete that assessment. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  // DSAR access: download everything we hold for this user as JSON.
  const onExport = async () => {
    setBusy(true);
    setNotice(null);
    try {
      const data = isSampleMode
        ? { exportedAt: new Date().toISOString(), assessments: [{ session: sampleHistory[0], report: sampleReport }] }
        : await api.exportMyData();
      downloadJson('fluentai-my-data.json', data);
    } catch {
      setNotice('Could not export your data. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const rows = (history ?? []).filter((s) => !hidden.has(s.assessmentId));

  return (
    <main className="mx-auto max-w-[1180px] px-8 pb-[72px] pt-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[42px] font-bold leading-[1.04] tracking-display text-code">My assessments</h1>
          <p className="mt-2 max-w-[62ch] text-[16px] leading-[1.55] text-storm">
            Every assessment you have started or completed. Reopen a report, or pick up one you left in progress.
          </p>
        </div>
        <Button variant="ghost" onClick={onExport}>
          <Download size={16} /> Export my data
        </Button>
      </div>

      {notice && <p className="mt-4 text-[13px] text-critical">{notice}</p>}

      <div className="mt-[34px] animate-fadeUp">
        <PageLoader show={loading} label="Loading your assessments…" />

        {!loading && rows.length === 0 && (
          <div className="rounded-card border border-panel bg-subtle p-8 text-center">
            <p className="text-[15px] font-semibold text-code">No assessments yet</p>
            <p className="mt-1 text-[13.5px] text-storm">Start one from the Welcome screen and it will appear here.</p>
          </div>
        )}

        {!loading && rows.length > 0 && (
          <ul className="space-y-4">
            {rows.map((s) => {
              const st = STATUS[s.status] ?? STATUS.created;
              const done = s.status === 'completed';
              const resumable = s.status === 'in_progress' || s.status === 'created';
              return (
                <li
                  key={s.assessmentId}
                  className="flex flex-wrap items-center justify-between gap-4 rounded-card border border-panel bg-white px-[26px] py-[22px]"
                >
                  <div>
                    <div className="flex items-center gap-2.5">
                      <span
                        className="inline-flex h-2.5 w-2.5 rounded-full"
                        style={{ background: st.color }}
                        aria-hidden="true"
                      />
                      <span className="text-[17px] font-bold text-code">{st.label}</span>
                      <span className="text-[14px] text-storm">· Stage {s.stage} of 3</span>
                    </div>
                    <div className="mt-1 text-[14px] text-storm">Started {fmtDate(s.startedAt)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {done ? (
                      <Button variant="ghost" onClick={() => openReport(s.assessmentId)}>
                        <FileText size={16} /> View report
                      </Button>
                    ) : resumable ? (
                      <Button onClick={() => resumeAssessment(s.assessmentId)}>
                        <RotateCcw size={16} /> Continue
                      </Button>
                    ) : null}
                    <button
                      onClick={() => onDelete(s.assessmentId)}
                      disabled={busy}
                      aria-label="Delete this assessment"
                      title="Delete this assessment"
                      className="rounded-ctl border border-panel p-2.5 text-storm transition-colors hover:border-critical hover:text-critical disabled:opacity-40"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </main>
  );
}
